import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Alert,
  Breadcrumbs,
  CircularProgress,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router";

import { useAuth } from "@/providers";
import { useHasOrgRole } from "@/providers/auth/hooks";
import { SlackIcon } from "@/Settings/Integrations/IntegrationIcon";
import { PageLayout } from "@/shared/components/PageLayout";
import { DefaultPremiumOrgOnlyFallback } from "@/shared/components/PremiumOrgOnly";
import { OrganizationTier } from "@/shared/domain/orgs/OrgRecord";
import { useSlackConnections } from "@/shared/domain/slack/hooks";
import { Button } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";

import { Connection } from "./Connection";
import { ConnectionStatus } from "./ConnectionStatus";
import styles from "./SlackIntegration.module.css";
import { useConnectSlackPopup } from "./useConnectSlackPopup";

/**
 * Whether the viewer may manage the integration, or `undefined` when that cannot be
 * determined (`myOrgRoles === null` here means the roles fetch failed — the pristine loading
 * state never renders past `AuthenticationLayout`).
 *
 * `useHasOrgRole` answers `false` in that degraded state too, so trusting it directly
 * would tell an admin they are not one. `myOrgRoles` is the same fetch `useHasOrgRole`
 * reads, so it is the right thing to probe for that degraded state.
 */
function useCanManageSlack(): boolean | undefined {
  const isAdminUser = useHasOrgRole("admin");
  const { myOrgRoles } = useAuth();

  if (isAdminUser) {
    return true;
  }
  return myOrgRoles === null ? undefined : false;
}

/**
 * The page's identity, then whichever action it offers. The action sits under the description
 * rather than opposite the title: it is the one thing a first-time visitor is looking for, and
 * the far corner of the header is where it is least likely to be found.
 */
function PageHeading({ action }: { action?: React.ReactNode }) {
  return (
    <>
      <div className={styles.headerWithIcon}>
        <SlackIcon size={32} />
        Slack Integration
      </div>

      <p className={styles.description}>
        Connect a Slack workspace so Roboto agents can post updates to channels
        your team approves.
      </p>

      {action}
    </>
  );
}

/**
 * The org's Slack integration page: connect a workspace, pick the channels agents may post
 * into, disconnect. Connect opens a popup that lands on `SlackOAuthCallback` and posts back
 * here; if blocked, the whole window goes.
 */
export function SlackIntegration() {
  const { currentOrganization } = useAuth();
  // Undefined when no org is stored. Everything below the heading is org-scoped, so that
  // case renders the page and stops rather than resolving an org that does not exist.
  const orgId = currentOrganization?.org_id;

  return (
    <PageLayout
      title="Roboto - Slack Integration"
      headerContentLeft={
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={styles.breadcrumbs}
        >
          <Link to={Goto.route.Settings.Root} className={styles.breadcrumbLink}>
            <SettingsIcon fontSize="small" />
            <span>Settings</span>
          </Link>
          <Link
            to={Goto.route.Settings.Integrations.Root}
            className={styles.breadcrumbLink}
          >
            <span>Integrations</span>
          </Link>
          <span className={styles.breadcrumbCurrent}>Slack</span>
        </Breadcrumbs>
      }
    >
      {orgId === undefined ? (
        <>
          <PageHeading />
          <div className={styles.body}>
            <div className={styles.emptyState}>
              <Typography variant="body2" color="text.secondary">
                No organization is selected, so the Slack integration cannot be
                loaded.
              </Typography>
            </div>
          </div>
        </>
      ) : (
        <OrgSlackIntegration orgId={orgId} />
      )}
    </PageLayout>
  );
}

/** The integration as it stands for one org. */
function OrgSlackIntegration({ orgId }: { orgId: string }) {
  const { currentOrganization } = useAuth();

  // Mirrors the ORG_ADMIN_ONLY rule on the connect, disconnect, and allowlist-write routes.
  const canManage = useCanManageSlack();
  // Connect and allowlist edits are premium-gated server-side; disconnect deliberately is not,
  // so a downgraded org can still sever its workspaces here.
  const isPremiumOrg = currentOrganization?.tier === OrganizationTier.Premium;

  const { data: connections, isLoading, error } = useSlackConnections(orgId);
  const { connect, status, dismiss } = useConnectSlackPopup(orgId);

  const allConnections = connections ?? [];
  const isEmpty = !isLoading && error === null && allConnections.length === 0;
  const canConnect = canManage === true && isPremiumOrg && isEmpty;

  return (
    <>
      <PageHeading
        action={
          canConnect && (
            <Button
              variant="contained"
              onClick={connect}
              loading={
                status.phase === "starting" || status.phase === "waiting"
              }
            >
              Connect to Slack
            </Button>
          )
        }
      />

      <div className={styles.body}>
        {isLoading && (
          <div className={styles.loadingRow}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading Slack connection…</Typography>
          </div>
        )}

        {error !== null && (
          <Alert severity="error">
            Could not load the Slack connection: {userFacingErrorMessage(error)}
          </Alert>
        )}

        <ConnectionStatus status={status} onDismiss={dismiss} />

        {/* An admin who can act needs no empty-state copy — the Connect button says it. Each
            branch below covers a viewer that button does not render for. */}
        {isEmpty && canManage === false && (
          <div className={styles.emptyState}>
            <Typography variant="body2" color="text.secondary">
              Ask an organization admin to connect a Slack workspace — only
              admins can manage this integration.
            </Typography>
          </div>
        )}

        {isEmpty && canManage === undefined && (
          <div className={styles.emptyState}>
            <Typography variant="body2" color="text.secondary">
              Your role could not be determined, so the integration is shown
              read-only.
            </Typography>
          </div>
        )}

        {isEmpty && canManage === true && !isPremiumOrg && (
          <DefaultPremiumOrgOnlyFallback />
        )}

        {!isPremiumOrg && allConnections.length > 0 && (
          <Alert severity="info">
            Connecting a workspace and editing the channel allowlist require the
            premium tier. Existing workspaces can still be disconnected.
          </Alert>
        )}

        {allConnections.map((connection) => (
          <Connection
            key={`${connection.enterprise_id}/${connection.team_id}`}
            orgId={orgId}
            connection={connection}
            canManage={canManage === true}
            canEditAllowlist={isPremiumOrg}
          />
        ))}
      </div>
    </>
  );
}
